import { useMemo, useState } from "react";
import { toast } from "./Toast.tsx";

export type SampleLang = "curl" | "js" | "python" | "go";

const LANGS: { value: SampleLang; label: string }[] = [
  { value: "curl", label: "cURL" },
  { value: "js", label: "JavaScript" },
  { value: "python", label: "Python" },
  { value: "go", label: "Go" },
];

/**
 * Placeholder API key shown in copy/paste samples. Deliberately NOT the live
 * demo secret — users paste their own `cc_live_…` key here.
 */
const PLACEHOLDER_KEY = "cc_live_YOUR_API_KEY";

function snippet(method: string, url: string, lang: SampleLang, requiresKey: boolean): string {
  switch (lang) {
    case "curl":
      return requiresKey
        ? `curl "${url}" \\\n  -H "Authorization: Bearer ${PLACEHOLDER_KEY}"`
        : `curl "${url}"`;
    case "js":
      return [
        `const res = await fetch("${url}", {`,
        `  method: "${method}",`,
        ...(requiresKey ? [`  headers: { Authorization: "Bearer ${PLACEHOLDER_KEY}" },`] : []),
        `});`,
        `const data = await res.json();`,
        `console.log(data);`,
      ].join("\n");
    case "python":
      return [
        `import requests`,
        ``,
        `res = requests.${method.toLowerCase()}(`,
        `    "${url}",`,
        ...(requiresKey ? [`    headers={"Authorization": "Bearer ${PLACEHOLDER_KEY}"},`] : []),
        `)`,
        `print(res.json())`,
      ].join("\n");
    case "go":
      return [
        `req, _ := http.NewRequest("${method}",`,
        `\t"${url}", nil)`,
        ...(requiresKey ? [`req.Header.Set("Authorization", "Bearer ${PLACEHOLDER_KEY}")`, ``] : []),
        `res, _ := http.DefaultClient.Do(req)`,
        `defer res.Body.Close()`,
        `body, _ := io.ReadAll(res.Body)`,
        `fmt.Println(string(body))`,
      ].join("\n");
  }
}

/**
 * "Use this in your code" — multi-language samples for the built request.
 * `requiresKey` is false for public endpoints (e.g. /countries): the samples
 * then omit the Authorization header entirely instead of telling users to pass
 * a key the endpoint ignores.
 */
export function CodeSamples({
  method,
  url,
  requiresKey = true,
}: {
  method: string;
  url: string;
  requiresKey?: boolean;
}) {
  const [lang, setLang] = useState<SampleLang>("curl");
  const code = useMemo(() => snippet(method, url, lang, requiresKey), [method, url, lang, requiresKey]);

  function copy() {
    void navigator.clipboard?.writeText(code).catch(() => {});
    toast("Code copied", true);
  }

  return (
    <div className="card mt rise">
      <div className="card-head">
        <h2>Use this in your code</h2>
        <div className="segmented" style={{ width: "fit-content" }} role="tablist" aria-label="Language">
          {LANGS.map((l) => (
            <button
              key={l.value}
              type="button"
              role="tab"
              aria-selected={lang === l.value}
              className={lang === l.value ? "active" : ""}
              onClick={() => setLang(l.value)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      <div className="card-pad">
        <pre
          className="mono"
          style={{ margin: 0, overflowX: "auto", fontSize: "12.5px", lineHeight: 1.7, color: "var(--text-muted)" }}
        >
          {code}
        </pre>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" }}>
          <button className="btn btn-sm" onClick={copy}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" />
            </svg>
            Copy
          </button>
          <span className="hint">
            {requiresKey ? (
              <>Replace <span className="mono">{PLACEHOLDER_KEY}</span> with your own live API key.</>
            ) : (
              <>This endpoint is public. No API key required.</>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
